/**
 * TractionPreview — the visible "preview" side of the Monetize Cockpit.
 *
 * Renders the unified Revenue Plan (4 sections focused on compressing
 * time-to-first-dollar): ICP · Positioning · Pricing · Distribution.
 * Driven by `outputs.revenue_plan.data` (single-pass LLM, auto-triggered
 * once the dataroom is generated). PillarMissionZone stays on top as the
 * long-horizon North-Star layer.
 */
import React, { useState } from 'react';
import {
  Sparkles, FileText, Loader2,
  Wrench, PenTool, Briefcase, Flame, Compass as PathIcon,
} from 'lucide-react';
import RevenuePlanZones from './RevenuePlanZones';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function TractionPreview({
  venture,          // monetize project doc
  outputs,          // outputs object keyed by module: { dataroom, revenue_plan, ... }
  palette,
  isMaterializing,  // boolean — true while Aria is generating the dataroom
  northStars,       // [{ pillar, agent_name, ... }, ...]
  onUpdatePillar,   // (pillar, patch) => Promise
  onOpenDocs,
}) {
  const dataroom = outputs?.dataroom?.data;
  const revenuePlan = outputs?.revenue_plan?.data;
  const monetizeId = venture?.monetize_id;

  const [regenerating, setRegenerating] = useState(false);

  // Auto-pending while the dataroom exists but the revenue plan hasn't finished
  // generating yet (the background task is in flight).
  const waitingForPlan = !!dataroom && !revenuePlan && !regenerating;
  const planLoading = isMaterializing || waitingForPlan || regenerating;

  const regeneratePlan = async () => {
    if (!monetizeId || regenerating) return;
    setRegenerating(true);
    try {
      await fetch(`${API}/api/monetize/projects/${monetizeId}/revenue-plan`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate: true }),
      });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      data-testid="traction-preview"
      style={{
        flex: 1, height: '100%', overflowY: 'auto',
        background: palette.bg, color: palette.text,
        position: 'relative',
      }}
    >
      {/* Ambient glow */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(60% 50% at 50% 0%, ${palette.accent}10, transparent 70%),
                     radial-gradient(40% 40% at 90% 100%, ${palette.accent2}0A, transparent 70%)`,
      }} />

      <div style={{ position: 'relative', padding: '28px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
        {/* Header bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 24,
        }}>
          <div>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: palette.textDim, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <Sparkles size={11} color={palette.accent} /> Revenue plan
            </div>
            <h2 style={{
              fontSize: 22, fontWeight: 800, color: palette.text, marginTop: 4, marginBottom: 0,
              letterSpacing: '-0.015em',
            }}>
              {venture?.name || 'Your venture'}
            </h2>
          </div>
          <button
            onClick={onOpenDocs}
            data-testid="open-docs-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px',
              background: `${palette.accent}1F`, color: palette.accent,
              border: `1px solid ${palette.accent}55`,
              cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 8,
            }}
          >
            <FileText size={12} /> Show the docs
          </button>
        </div>

        {/* PillarMissionZone — strategic North-Stars (kept above the plan) */}
        {northStars && northStars.length > 0 && (
          <PillarMissionZone
            pillars={northStars}
            palette={palette}
            onUpdatePillar={onUpdatePillar}
          />
        )}

        {/* The unified Revenue Plan (4 sections) */}
        <RevenuePlanZones
          plan={revenuePlan}
          loading={planLoading}
          onRegenerate={dataroom ? regeneratePlan : null}
        />

        {/* Pre-dataroom hint */}
        {!dataroom && !isMaterializing && (
          <div style={{
            marginTop: 18,
            padding: '14px 18px',
            background: `${palette.accent}10`,
            border: `1px dashed ${palette.accent}55`,
            borderRadius: 12,
            fontSize: 12.5,
            color: palette.textDim, lineHeight: 1.55,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Loader2 size={13} color={palette.accent} />
            <span>
              Ask Aria in the chat to scan your venture. Once the dataroom is ready,
              the Revenue Plan will materialize here automatically.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================ Pillar Mission Zone ============================ */
// Kept inline to avoid a separate file. The original PillarMissionZone code
// from this module continues below.

/* =================== PILLAR MISSION ZONE (North Stars) ===================
   The 4 strategic cards (Build/Growth/Content/Revenue). Each card shows :
   · pillar identity (agent name + role + tagline)
   · the North Star metric + 30/60/90d targets (strategic, weekly-edit)
   · a tiny ⚡ live chip (current_value / 30d goal) — placeholder for HQ live link
   No tactical data here (no specific leads / posts / commits — those live in HQ).
   ====================================================================== */

const PILLAR_THEME = {
  build:   { color: '#7C3AED', soft: '#A78BFA', icon: Wrench,    gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)' },
  growth:  { color: '#FFB347', soft: '#FFD166', icon: PathIcon,  gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)' },
  content: { color: '#FF6B1A', soft: '#FF9F40', icon: PenTool,   gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)' },
  revenue: { color: '#C026D3', soft: '#E879F9', icon: Briefcase, gradient: 'linear-gradient(135deg, #E879F9 0%, #C026D3 100%)' },
};

function PillarMissionZone({ pillars, palette, onUpdatePillar }) {
  return (
    <div data-testid="pillar-mission-zone" style={{ marginBottom: 28 }}>
      <ZoneHeader
        palette={palette}
        icon={<Flame size={14} />}
        title="Pillar Mission Cards"
        subtitle="The 4 North Stars · strategic targets only (live values in HQ Execution)"
      />
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 12,
      }}>
        {pillars.map(p => (
          <PillarCard
            key={p.pillar}
            data={p}
            palette={palette}
            onUpdate={onUpdatePillar ? (patch) => onUpdatePillar(p.pillar, patch) : null}
          />
        ))}
      </div>
    </div>
  );
}

function ZoneHeader({ palette, icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 10, fontWeight: 800, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: palette.accent,
        marginBottom: 4,
      }}>
        {icon}
        <span>{title}</span>
      </div>
      <div style={{ fontSize: 12, color: palette.textDim, letterSpacing: '-0.005em' }}>
        {subtitle}
      </div>
    </div>
  );
}

function PillarCard({ data, palette, onUpdate }) {
  const theme = PILLAR_THEME[data.pillar] || PILLAR_THEME.build;
  const Icon = theme.icon;
  const pctOfGoal = data.goal_30d > 0
    ? Math.min(100, Math.round((data.current_value / data.goal_30d) * 100))
    : 0;

  return (
    <div
      data-testid={`pillar-card-${data.pillar}`}
      style={{
        position: 'relative',
        padding: 16,
        background: palette.surface || `${palette.text}06`,
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 14px 32px ${theme.color}26`;
        e.currentTarget.style.borderColor = `${theme.color}55`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = palette.border;
      }}
    >
      {/* Top signature band */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: theme.gradient,
      }} />

      {/* Glow corner */}
      <div style={{
        position: 'absolute', top: -40, right: -40,
        width: 130, height: 130, borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.color}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: `${theme.color}1c`,
          border: `1px solid ${theme.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: theme.color,
        }}>
          <Icon size={14} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: theme.color,
          }}>
            {data.agent_name}
          </div>
          <div style={{
            fontSize: 14, fontWeight: 800, color: palette.text,
            letterSpacing: '-0.015em', lineHeight: 1.1,
            background: theme.gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            {data.role}
          </div>
        </div>
      </div>

      {/* North Star metric */}
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: palette.textDim,
        marginBottom: 4,
      }}>
        North Star
      </div>
      <div style={{
        fontSize: 13, fontWeight: 700, color: palette.text,
        marginBottom: 14, lineHeight: 1.3,
        letterSpacing: '-0.005em',
      }}>
        {data.metric_name}
      </div>

      {/* Targets row */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        marginBottom: 12,
      }}>
        <TargetCell
          palette={palette}
          theme={theme}
          label="30 days"
          value={data.goal_30d}
          unit={data.unit}
        />
        <TargetCell
          palette={palette}
          theme={theme}
          label="90 days"
          value={data.goal_90d}
          unit={data.unit}
        />
      </div>

      {/* Live chip + budget */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 10,
        borderTop: `1px solid ${palette.border}`,
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 9px',
          background: `${theme.color}14`,
          border: `1px solid ${theme.color}40`,
          borderRadius: 999,
          fontSize: 10, fontWeight: 700,
          color: theme.color,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '0.02em',
        }} title="Live value vs 30-day goal (full panel in HQ Execution)">
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: theme.color,
            boxShadow: `0 0 8px ${theme.color}`,
          }} />
          ⚡ {formatMetric(data.current_value)} / {formatMetric(data.goal_30d)}
          <span style={{ opacity: 0.55 }}>· {pctOfGoal}%</span>
        </div>
        <div style={{
          fontSize: 10, fontWeight: 700,
          color: palette.textDim,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '0.02em',
        }} title="Monthly external budget cap for this pillar">
          ${data.budget_monthly_usd}/mo
        </div>
      </div>
    </div>
  );
}

function TargetCell({ palette, theme, label, value, unit }) {
  return (
    <div style={{
      padding: '9px 11px',
      background: `${theme.color}0b`,
      border: `1px solid ${theme.color}22`,
      borderRadius: 9,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: palette.textDim,
        marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 17, fontWeight: 900,
        color: palette.text,
        letterSpacing: '-0.02em',
        lineHeight: 1,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {formatMetric(value)}
      </div>
      <div style={{
        fontSize: 9, fontWeight: 600,
        color: palette.textDim,
        marginTop: 2,
        letterSpacing: '0.02em',
      }}>
        {unit}
      </div>
    </div>
  );
}

function formatMetric(n) {
  const num = typeof n === 'number' ? n : parseFloat(n);
  if (!Number.isFinite(num)) return String(n);
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 10_000) return (num / 1_000).toFixed(0) + 'K';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(num);
}
